import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import {
  fetchOrganisation,
  Organisation,
  deleteOrganisation,
} from "../../app/services/OrganisationService";
import { fetchStates } from "../../app/services/StatesService";
import { toast } from "react-toastify";
import PageWrapper from "../../app/shared/common/PageWrapper";
import TableActions from "../../app/shared/common/table/TableAction";
import CreateTable from "../../app/shared/common/table/CreateTable";
import SearchBox from "../../app/shared/common/SearchBox";
import DropDown from "../../app/shared/common/DropDown";
const tableColumns = [
  { key: "name", label: "Name" },
  { key: "legalname", label: "Legal Name" },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile" },
  { key: "state.name", label: "State" },
  { key: "address", label: "Address" },
  { key: "pin_code", label: "Pincode" },
  { key: "pan_number", label: "Pan" },
];
const OrganisationList: React.FC = () => {
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [filteredOrg, setFilteredOrg] = useState<Organisation[]>([]);
  const [stateList, setStateList] = useState<{ id: number; name: string }[]>(
    []
  );
  const [stateId, setStateId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrgtId, setSelectedOrgId] = useState<number | null>(null);

  useEffect(() => {
    loadData();
    loadStates();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetchOrganisation();
      setOrganisations(res.data || []);
      setFilteredOrg(filterRows(res.data, searchTerm, stateId));
    } catch (err) {
      toast.error("Failed to load organisations");
    } finally {
      setLoading(false);
    }
  };

  const filterRows = (rows: Organisation[], query: string, stateId: string) => {
    return rows.filter((org) => {
      const matchesSearch =
        org.name?.toLowerCase().includes(query.toLowerCase()) ||
        org.legalname?.toLowerCase().includes(query.toLowerCase()) ||
        org.email?.toLowerCase().includes(query.toLowerCase());

      const matchesState = stateId
        ? String(org.stateid ?? "") === String(stateId)
        : true;

      return matchesSearch && matchesState;
    });
  };

  const loadStates = async () => {
    try {
      const res = await fetchStates();
      setStateList(res.data || []);
    } catch (err) {
      toast.error("Failed to load states");
    }
  };

  const handleOpenDialog = (id: number) => {
    setSelectedOrgId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedOrgId(null);
    setOpenDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedOrgtId) return;
    try {
      await deleteOrganisation(selectedOrgtId);
      setOrganisations((prevOrgs) => {
        const updatedList = prevOrgs.filter((org) => org.id !== selectedOrgtId);

        setFilteredOrg(filterRows(updatedList, searchTerm, stateId));

        return updatedList;
      });
      toast.success("Orgnisation deleted successfully.");
    } catch (err) {
      toast.error("Something went wrong while deleting.");
    } finally {
      handleCloseDialog();
    }
  };

  useEffect(() => {
    setFilteredOrg(filterRows(organisations, searchTerm, stateId));
  }, [searchTerm, stateId, organisations]);
  return (
    <PageWrapper
      title={"Organisation"}
      navigateTo={"/organisation/add"}
      buttonTitle={"Add Organisation"}
      actionName={"organisation"}
      open={openDialog}
      onClose={handleCloseDialog}
      onConfirm={handleConfirmDelete}
    >
      <Box display="flex" gap={2} mb={2}>
        <SearchBox value={searchTerm} onChange={setSearchTerm} />
        <DropDown
          label="Filter by State"
          value={stateId}
          onChange={setStateId}
          options={stateList}
          allLabel="All States"
        />
      </Box>
      <CreateTable
        loading={loading}
        data={filteredOrg}
        columns={tableColumns}
        renderActions={(org) => (
          <TableActions
            navigateTo={`/organisation/update/${org.id}`}
            onClick={() => handleOpenDialog(org.id)}
          />
        )}
      />
    </PageWrapper>
  );
};

export default OrganisationList;
